'use client'

// Cloudflare Turnstile, mounted explicitly for the Twetch register door.
//
// The widget embeds TWETCH'S site key — registration is Twetch's endpoint,
// and its verification runs against Twetch's allowed-hostname list. That
// list includes ordinalswallet.com; previews and localhost are NOT on it,
// so on those hosts the widget itself errors — by design. onError is how
// the join card learns that and says so honestly instead of dangling a
// button that can never work.

import React from 'react'

/// Twetch's public Turnstile site key (site keys are public by design —
/// this is the same value twetch.com ships in its own bundle).
const TWETCH_TURNSTILE_SITE_KEY = '0x4AAAAAAECy0NvfA77d4cLG'

const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

interface TurnstileApi {
	render: (
		el: HTMLElement,
		opts: {
			sitekey: string
			action?: string
			theme?: 'dark' | 'light' | 'auto'
			appearance?: 'always' | 'execute' | 'interaction-only'
			callback: (token: string) => void
			'error-callback'?: () => void
			'expired-callback'?: () => void
		}
	) => string
	remove: (widgetId: string) => void
}

declare global {
	interface Window {
		turnstile?: TurnstileApi
	}
}

function loadScript(): Promise<TurnstileApi> {
	return new Promise((resolve, reject) => {
		if (window.turnstile) {
			resolve(window.turnstile)
			return
		}
		const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
		const script = existing ?? document.createElement('script')
		const settle = () => {
			if (window.turnstile) {
				resolve(window.turnstile)
			} else {
				reject(new Error('turnstile failed to initialise'))
			}
		}
		script.addEventListener('load', settle, { once: true })
		script.addEventListener('error', () => reject(new Error('turnstile script failed')), {
			once: true
		})
		if (!existing) {
			script.id = SCRIPT_ID
			script.src = SCRIPT_SRC
			script.async = true
			document.head.appendChild(script)
		} else if (window.turnstile) {
			settle()
		}
	})
}

/// Renders the challenge and reports token state upward. `onToken(null)`
/// fires on expiry so the join button can re-disable instead of submitting
/// a dead token.
export default function TwetchTurnstile({
	onToken,
	onError,
	appearance = 'always',
	resetKey = 0
}: {
	onToken: (token: string | null) => void
	onError: () => void
	appearance?: 'always' | 'execute' | 'interaction-only'
	/// Bump to re-mount the widget for a fresh token — Turnstile tokens are
	/// single-use, so a rejected attempt has already spent the old one.
	resetKey?: number
}) {
	const hostRef = React.useRef<HTMLDivElement | null>(null)
	// The callbacks land in refs so a re-render never re-mounts the widget.
	const onTokenRef = React.useRef(onToken)
	const onErrorRef = React.useRef(onError)
	React.useEffect(() => {
		onTokenRef.current = onToken
		onErrorRef.current = onError
	})

	React.useEffect(() => {
		let widgetId: string | null = null
		let cancelled = false
		void loadScript()
			.then((turnstile) => {
				if (cancelled || !hostRef.current) {
					return
				}
				widgetId = turnstile.render(hostRef.current, {
					sitekey: TWETCH_TURNSTILE_SITE_KEY,
					// Twetch's verifier only accepts tokens minted for this action.
					action: 'signup',
					theme: 'dark',
					appearance,
					callback: (token) => onTokenRef.current(token),
					'expired-callback': () => onTokenRef.current(null),
					'error-callback': () => onErrorRef.current()
				})
			})
			.catch(() => {
				if (!cancelled) {
					onErrorRef.current()
				}
			})
		return () => {
			cancelled = true
			if (widgetId && window.turnstile) {
				try {
					window.turnstile.remove(widgetId)
				} catch {
					// already gone
				}
			}
		}
	}, [appearance, resetKey])

	return (
		<div
			ref={hostRef}
			data-testid="alkanes-twetch-turnstile"
			className={appearance === 'always' ? 'min-h-[65px]' : 'min-h-0'}
		/>
	)
}
